import { Component, computed, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { StoreService } from '../../../store/store.service';
import { FADE, GROW } from '../../animations/animations';
import { SafeHtml } from '../../pipes/SafeHtml.pipe';
import { CommonService } from '../../services/common.service';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { ModalService } from '../../services/modal.service';
import { SidePanelService } from '../../services/side-panel.service';
import { ButtonComponent } from '../button/button.component';
import { CardComponent } from '../card/card.component';
import { NavTabsComponent } from '../nav-tabs/nav-tabs.component';
import { PriceBreakdownComponent } from '../price-breakdown/price-breakdown.component';
import { TagComponent } from '../tag/tag.component';
import { SkeletonLoaderItemComponent } from '../skeleton-loader-item/skeleton-loader-item';

@Component({
  standalone: true,
  imports: [
    CardComponent,
    SafeHtml,
    TagComponent,
    ButtonComponent,
    NavTabsComponent,
    PriceBreakdownComponent,
    SkeletonLoaderItemComponent,
  ],
  selector: 'app-room-rate-card',
  templateUrl: 'room-rate-card.component.html',
  styleUrl: 'room-rate-card.component.scss',
  host: {
    '[@grow]': 'true',
  },
  animations: [GROW, FADE],
})
export class RoomRateCardComponent {
  roomRateDetailsContentTemplate = viewChild<TemplateRef<any>>('roomRateDetailsContent');

  // Inputs
  public readonly data = input.required<any>();
  public readonly index = input.required<number>();
  public readonly noPadding = input<boolean>(false);
  public readonly noMarginBottom = input<boolean>(false);
  public readonly noBorder = input<boolean>(false);

  // Optional Inputs
  public readonly isChild = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);

  // Outputs
  public readonly onRoomRateSelected = output<void>();

  // Signals
  public readonly roomRateDetailsNavTabs = signal([
    { key: 'RATE_DETAILS', value: 'Rate details' },
    { key: 'PRICE_BREAKDOWN', value: 'Price breakdown' },
  ]);
  public readonly roomRateDetailsActiveTab = signal<'RATE_DETAILS' | 'PRICE_BREAKDOWN'>('RATE_DETAILS');

  // Computed Signals
  public readonly isMobileScreen = computed(() => {
    return (this._storeSvc.res()?.mobile || this._storeSvc.res()?.tablet) ?? false;
  });
  public readonly showDPRMessage = computed(() => this._storeSvc.propInfo()?.displayDprTagline ?? false);
  public readonly pricing = computed(() => {
    if (this.isLoading()) {
      return null;
    }

    const roomRate = this.data();
    const localeObj = this._storeSvc.localeObj();
    const currency = this._storeSvc.userStore.currency();
    const showPriceInclusiveOfFees = this._shoppingCartSvc.showTaxesInDisplayPrice();

    // Upsell Price
    const hasUpsellPrice = roomRate?.isUpsellRoom && !!roomRate?.upsellPriceInfo;
    const upsellPrice = hasUpsellPrice
      ? roomRate?.upsellPriceInfo?.averageUpsellPriceByCurrency[currency.code] ||
        roomRate?.upsellPriceInfo?.totalUpsellPriceByCurrency[currency.code]
      : null;

    // Complimentary Text
    const isFullComp = roomRate.hasComplimentaryNight && roomRate.isFullyComplimentary;
    const isPartialComp = !roomRate.isFullyComplimentary && roomRate.hasComplimentaryNight;
    let compText = '';
    if (isFullComp) {
      compText = localeObj.tf_2_RoomList_Complimentary_text;
    } else if (isPartialComp) {
      compText = localeObj.tf_2_RoomList_availableComplimentaryNight_text;
    }

    const guestSummary = this._storeSvc.appStore.guestSummary();
    const room1Guests = guestSummary?.guests[0];
    const guestsCount = room1Guests?.adults + (room1Guests?.children?.length || 0);
    const los = guestSummary?.los || 0;
    const lowestUnitAddOnPrice = roomRate?.lowestUnitAddOnPrice[currency.code] || 0;

    // Average Rate Plan Price
    const avgPrice = this._shoppingCartSvc.getAvgRatePlanPrice(roomRate, currency.code, showPriceInclusiveOfFees);
    const avgRate = avgPrice + (lowestUnitAddOnPrice * guestsCount) / los;
    const hasAvgRate = avgRate > 0;

    // Discounted Rate Plan Price
    const discountedPrice = this._shoppingCartSvc.getDiscountedRatePlanPrice(
      roomRate,
      currency.code,
      showPriceInclusiveOfFees,
    );
    const discountedRate = discountedPrice + (lowestUnitAddOnPrice * guestsCount) / los;
    const hasDiscountedRate = discountedRate > 0;

    // Tax Display
    const taxDisplay = this._shoppingCartSvc.getTaxDisplay(roomRate, currency.code, currency.symbol, localeObj);

    return {
      hasUpsellPrice,
      upsellPrice,
      isFullComp,
      isPartialComp,
      compText,
      hasAvgRate,
      avgRate,
      hasDiscountedRate,
      discountedRate: hasDiscountedRate ? discountedRate : avgRate,
      taxDisplay,
    };
  });

  constructor(
    public readonly _storeSvc: StoreService,
    public readonly _commonSvc: CommonService,
    public readonly _shoppingCartSvc: ShoppingCartService,
    private readonly _modalSvc: ModalService,
    private readonly _sidePanelSvc: SidePanelService,
  ) {}

  public openRoomRateDetails() {
    if (this.isMobileScreen()) {
      this._sidePanelSvc.openSidePanel(this.roomRateDetailsContentTemplate(), { title: this.data()?.name });
    } else {
      this._modalSvc.openModal(this.roomRateDetailsContentTemplate(), {
        title: this.data()?.name,
        size: 'default',
      });
    }
  }

  public closeRoomRateDetails() {
    if (this.isMobileScreen()) {
      this._sidePanelSvc.closeSidePanel();
    } else {
      this._modalSvc.closeModal();
    }

    setTimeout(() => {
      this.roomRateDetailsActiveTab.set('RATE_DETAILS');
    }, 1000);
  }

  public selectRate() {
    this.onRoomRateSelected.emit(this.data());
  }
}
